/**
 * Browser Notification Service
 * Handles browser push notifications using the Notification API
 */

class NotificationService {
  constructor() {
    this.permission = null;
    this.isSupported = 'Notification' in window;
    this.checkPermission();
  }

  /**
   * Check if notifications are supported and get current permission status
   */
  checkPermission() {
    if (!this.isSupported) {
      console.warn('Browser notifications are not supported');
      return 'denied';
    }

    this.permission = Notification.permission;
    return this.permission;
  }

  /**
   * Request permission from the user
   */
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Browser notifications are not supported');
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    if (this.permission === 'denied') {
      throw new Error('Notification permission has been denied. Please enable it in your browser settings.');
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  /**
   * Check if notifications are enabled by user preference
   */
  isEnabled() {
    const preference = localStorage.getItem('pushNotifications');
    return preference ? JSON.parse(preference) : false;
  }

  /**
   * Show a browser notification
   */
  async show(title, options = {}) {
    // Check if notifications are enabled in user preferences
    if (!this.isEnabled()) {
      console.log('Push notifications are disabled in settings');
      return null;
    }

    // Check if permission is granted
    if (this.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission not granted');
        return null;
      }
    }

    const defaultOptions = {
      icon: '/maveshiwala-cow-icon.svg',
      badge: '/maveshiwala-cow-icon.svg',
      tag: 'maveshi-notification',
      requireInteraction: false,
      silent: false,
      ...options,
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Auto close after 5 seconds if not requireInteraction
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      // Handle click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();

        // If a URL is provided, navigate to it
        if (defaultOptions.data?.url) {
          window.location.href = defaultOptions.data.url;
        }
      };

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      throw error;
    }
  }

  /**
   * Show notification for task reminder
   */
  async showTaskReminder(task) {
    return this.show(`Task: ${task.title}`, {
      body: task.description || 'You have a task to complete',
      icon: '/maveshiwala-cow-icon.svg',
      tag: `task-${task.id}`,
      data: {
        type: 'task',
        id: task.id,
        url: `/tasks`,
      },
    });
  }

  /**
   * Show notification for schedule reminder
   */
  async showScheduleReminder(schedule) {
    return this.show(`Schedule: ${schedule.title}`, {
      body: schedule.description || 'You have a scheduled event',
      icon: '/maveshiwala-cow-icon.svg',
      tag: `schedule-${schedule.id}`,
      data: {
        type: 'schedule',
        id: schedule.id,
        url: `/schedule`,
      },
    });
  }

  /**
   * Show generic notification
   */
  async showGeneric(title, message, url = null) {
    return this.show(title, {
      body: message,
      data: url ? { url } : null,
    });
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;

